import type { ApiAttendanceStaff } from '@/features/kitchen/api/getTodayAttendance'
import type { KitchenStaffMember } from '@/features/kitchen/kitchenStaff'
import type { StaffDayAttendanceRecord } from '@/features/kitchen/storage/attendanceStorage'
import { resolvePhotoUrl } from '@/shared/api/uploadPhoto'

function formatTimeLabel(iso: string): string {
  return new Intl.DateTimeFormat('id-ID', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso))
}

export function mapStaffFromApi(staff: ApiAttendanceStaff[]): KitchenStaffMember[] {
  return staff.map((s) => ({
    id: s.id,
    name: s.name,
    role: s.role,
    roleLabel: s.role_label,
    shiftStart: s.shift_start,
    shiftEnd: s.shift_end,
  }))
}

export function mapAttendanceRecordsFromApi(
  staff: ApiAttendanceStaff[],
  date: string,
): Record<string, StaffDayAttendanceRecord> {
  const records: Record<string, StaffDayAttendanceRecord> = {}

  for (const member of staff) {
    const record: StaffDayAttendanceRecord = {
      userId: member.id,
      date,
    }

    if (member.check_in_at) {
      record.checkIn = {
        at: member.check_in_at,
        timeLabel: formatTimeLabel(member.check_in_at),
        photoUrl: resolvePhotoUrl(member.check_in_photo),
        onTime: member.on_time,
      }
    }

    if (member.check_out_at) {
      record.checkOut = {
        at: member.check_out_at,
        timeLabel: formatTimeLabel(member.check_out_at),
        photoUrl: resolvePhotoUrl(member.check_out_photo),
      }
    }

    if (record.checkIn || record.checkOut) {
      records[member.id] = record
    }
  }

  return records
}

export function buildAttendanceSummaryFromRoster(
  roster: KitchenStaffMember[],
  records: Record<string, StaffDayAttendanceRecord>,
) {
  let checkedIn = 0
  let finished = 0
  const notCheckedIn: string[] = []
  const onShift: string[] = []

  for (const staff of roster) {
    const record = records[staff.id]
    if (!record?.checkIn) {
      notCheckedIn.push(staff.name)
      continue
    }
    checkedIn += 1
    if (record.checkOut) {
      finished += 1
    } else {
      onShift.push(staff.name)
    }
  }

  return {
    total: roster.length,
    checkedIn,
    finished,
    notCheckedIn,
    onShift,
    notCheckedInCount: notCheckedIn.length,
  }
}
